<template>
  <div class="print-tag-subheader">
    <subheader
      id="print-tag-main-view-subheader"
      class="alternative-subheader"
    >
      <div slot="filter-labels">
        <transition
          name="fade"
          mode="out-in"
        >
          <div
            v-if="!simpleTemplate"
            id="btnPrintTagFilter"
            @click="pushToFilter()"
          >
            <h2>
              {{ title }}
              <span
                v-for="(label, index) in filterLabels"
                :key="label"
              >
                <span class="filter-labels">{{ label }}</span><span v-if="index + 1 < filterLabels.length">, </span>
              </span>
            </h2>
          </div>
          <div
            v-else
            id="btnLeftCardDetails"
            class="batch-details-title"
            @click="
              $router.push({
                path: '/print-tag/batches/',
                query: {
                  startDate: $route.query.startDate,
                  endDate: $route.query.endDate,
                  packagingType: $route.query.packagingType
                }
              })
            "
          >
            <svg-loader src="/static/images/icon-turn-back.svg" />
            <h2>{{ title }}</h2>
          </div>
        </transition>
      </div>
      <div class="subheader-alternative-actions-container">
        <div
          v-if="searchButtonEnabled"
          class="subheader-actions-container__turn-back position-right"
          @click="pushToFilter()"
        >
          <svg-loader
            id="backToSamplesSearch"
            src="/static/images/icon-back-to-search.svg"
          />
        </div>
        <slot name="regular-buttons" />
      </div>

      <slot name="back-buttons" />

      <div
        :class="{
          'subheader-alternative-actions-container': filterButtonAtNewRow,
          'subheader-actions-search': filterButtonAtNewRow,
          'subheader-actions-container': !filterButtonAtNewRow
        }"
      >
        <div class="subheader-actions-container__turn-back">
          <slot name="search-container" />
        </div>
      </div>
    </subheader>

    <div class="width-responsiveness">
      <slot name="alert" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { convertDateToLabel, convertArrayFilterToLabel } from '@/utils/build-filter-labels'
import { formartDateFromArray } from '@/utils/date-utils'
import SubHeader from '@/components/common/SubHeader'
import strings from '@/strings'
import { BATCH_TOTAL_PACKAGINGS } from '@/enums'

export default {
  name: 'PrintTagSubheader',
  components: {
    subheader: SubHeader
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    simpleTemplate: {
      type: Boolean,
      default: false
    },
    filterButtonAtNewRow: {
      type: Boolean,
      default: false
    },
    searchButtonEnabled: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    filterLabels: []
  }),
  computed: {
    ...mapGetters(['currentBatchOpenIndex', 'getTodayDate', 'filesUploaded', 'profile', 'toggle']),
    today: function () {
      return formartDateFromArray(this.getTodayDate)
    }
  },
  watch: {
    $route(to, from) {
      this.buildFilterLabel()
    }
  },
  created() {
    this.buildFilterLabel()
  },
  methods: {
    ...mapActions(['setFilterShowOff']),
    pushToFilter() {
      this.setFilterShowOff(true)
      this.$router.push({
        path: '/print-tag/filter',
        query: this.$route.query
      })
      this.$analytics.pushAnalyticsEvent('ga-event', {
        category: 'Impressão de Etiquetas - Lote',
        action: 'Ir para o filtro',
        'id-user': this.profile.id,
        'id-lab': this.profile.id_laboratory,
        'name-lab': this.profile.laboratoryName
      })
    },
    buildFilterLabel() {
      this.filterLabels = []

      if (this.$route.query.startDate && this.$route.query.endDate) {
        this.filterLabels.push(
          convertDateToLabel(this.filterInitialStartDate(), this.filterInitialEndDate(), this.today)
        )
      }

      if (this.$route.query.integrated !== '' && this.$route.query.integrated !== undefined) {
        this.filterLabels.push(convertArrayFilterToLabel(this.$route.query.integrated, strings.originResultsFilter))
      }
      if (this.$route.query.printed !== '' && this.$route.query.printed !== undefined) {
        this.filterLabels.push(convertArrayFilterToLabel(this.$route.query.printed, strings.printedFilter))
      }
      if (this.$route.query.processingOrigin === 'RECOLLECT') {
        this.filterLabels.push('filtrada por amostras de recoletas')
      }
      if (this.$route.query.patientName) {
        this.filterLabels.push('paciente ' + this.$route.query.patientName)
      }
      if (this.$route.query.codeOriginLis) {
        let labelName = this.toggle.covax ? 'Requisition Number ' : 'OS LIS '
        this.filterLabels.push(labelName + this.$route.query.codeOriginLis)
      }
      if (this.$route.query.codeOriginAlvaro) {
        this.filterLabels.push('OS Alvaro ' + this.$route.query.codeOriginAlvaro)
      }
      if (this.$route.query.batchLabel) {
        this.filterLabels.push('LOTE N° ' + this.$route.query.batchLabel)
      }
      if (this.$route.query.collectionPointName) {
        this.filterLabels.push('posto de coleta ' + this.$route.query.collectionPointName)
      }
      if (this.$route.query.packagingType) {
        let packagings = [].concat(this.$route.query.packagingType)

        if (packagings.length < BATCH_TOTAL_PACKAGINGS) {
          packagings.forEach(packaging => {
            this.filterLabels.push(this.$strings.packagingStatus.find(x => x.id.toString() === packaging).name)
          })
        }
      }
      if (this.$route.query.idFile) {
        let _self = this
        let fileName = this.filesUploaded.filter(function (elem) {
          if (elem.id_file === parseInt(_self.$route.query.idFile)) {
            return elem.name
          }
        })
        this.filterLabels.push('do arquivo ' + fileName[0].name)
      }

      if (Object.keys(this.$route.query).length === 1) {
        this.filterLabels.push('todas')
      }
    },
    filterInitialStartDate() {
      if (this.$route.query.startDate || this.$route.query.startDate === '') {
        return this.$route.query.startDate
      } else {
        return this.today
      }
    },
    filterInitialEndDate() {
      if (this.$route.query.endDate || this.$route.query.endDate === '') {
        return this.$route.query.endDate
      } else {
        return this.today
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.print-tag-subheader {
  .container {
    display: flex;
  }

  .switcher-position {
    display: flex;
    margin: auto 0 auto auto;
  }

  button {
    font-family: 'Exo 2', sans-serif;
  }

  .batch-details-title {
    display: flex;
    cursor: pointer;

    h2 {
      margin: 0.7em;
    }

    .md-icon {
      height: 1px;
    }
  }
}
</style>
